import { Cell, Column, TableInstance, TableOptions, TableState, UseSortByOptions, UseSortByState, useSortBy, useTable } from "react-table";
import { FinOpsServiceDetailTableDisplayNames, IFinOpsBaseline, IFinOpsBudget, IFinOpsBudgetFilters, IFinOpsMonthlyBudgetAllocation, IFinOpsMonthlyBudgetAllocationRecord, IFinOpsServiceGEMMapping } from "../models/FinOpsBudgets";
import { getAllHistoricalBudgetVersion, getAllPendingBudgetAsks, getCostAndBudget, getFyBaseline, getLatestBudgetVersion, getMonthlyBudgetAllocation, getServiceGEMMapping, getServiceLevelBudget, getSubmitOverview, getUserApplicationRoles, initializeNewFinOpsBudgets, reviewFinOpsBudgets, reviewFinOpsBudgetsInBatch, signOffFromGM, updateFinOpsBudgets, updateMonthlyBudgetAllocation } from "../services/FinOpsService";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";

import { Endpoints } from "../utils/Constants";
import { IAppState } from "../store";
import { IColumnSelectorItem } from "../components/common/table/ColumnSelector";
import { Moment } from "moment";
import { useCategoryFilters } from "./useFilters";
import { useDateRange } from "./useDateSelector";
import { useSelector } from "react-redux";

export function useInitializeNewFinOpsBudgets(
    onSuccess: () => void,
    onError: (error: Error) => void
) {
    return useMutation({
        mutationFn:(data: IFinOpsBudget[]) => initializeNewFinOpsBudgets(data),
        onSuccess,
        onError
    });
}

export function useUpdateFinOpsBudgets() {
    return useMutation((data: IFinOpsBudget) => updateFinOpsBudgets(data));
}

export function useSignOffFromGM() {
    return useMutation((data: {fiscalYear: string, signOffTime: Moment}) => signOffFromGM(data.fiscalYear, data.signOffTime));
}

export function useReviewFinOpsBudgets() {
    return useMutation((data: IFinOpsBudget) => reviewFinOpsBudgets(data));
}

export function useReviewFinOpsBudgetsInBatch() {
    return useMutation(({ data, reviewStatus }: { data: number[]; reviewStatus: number }) => reviewFinOpsBudgetsInBatch(data, reviewStatus));
}

export function useGetAllPendingBudgetAsks(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetAllPendingBudgetAsks, globalFilters.filters, range.startDate, range.endDate],
        () => getAllPendingBudgetAsks(finOpsFilters)
    );
}

export function useGetAllHistoricalBudgetVersion(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetAllHistoricalBudgetVersion, globalFilters.filters, range.startDate, range.endDate],
        () => getAllHistoricalBudgetVersion(finOpsFilters)
    );
}

export function useGetLatestBudgetVersion(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetLatestBudgetVersion, globalFilters.filters, range.startDate, range.endDate],
        () => getLatestBudgetVersion(finOpsFilters)
    );
}

export function useGetSubmitOverview(finOpsFilters: IFinOpsBudgetFilters, startTime:Moment, endTime:Moment) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetSubmitOverview, globalFilters.filters, range.startDate, range.endDate],
        () => getSubmitOverview(finOpsFilters, startTime, endTime)
    );
}

export function useGetCostAndBudget(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetCostAndBudget, globalFilters.filters, range.startDate, range.endDate],
        () => getCostAndBudget(finOpsFilters)
    );
}

export function useGetServiceLevelBudget(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBudget[], Error>(
        [Endpoints.GetCostAndBudget, globalFilters.filters, range.startDate, range.endDate],
        () => getServiceLevelBudget(finOpsFilters)
    );
}

export function useGetMonthlyBudgetAllocation(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsMonthlyBudgetAllocation[], Error>(
        [Endpoints.GetMonthlyBudgetAllocation, globalFilters.filters, range.startDate, range.endDate],
        () => getMonthlyBudgetAllocation(finOpsFilters)
    );
}

export function useGetFyBaseline(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsBaseline[], Error>(
        [Endpoints.GetFyBaseline, globalFilters.filters, range.startDate, range.endDate],
        () => getFyBaseline(finOpsFilters)
    );
}

export function useGetServiceGEMMapping(finOpsFilters: IFinOpsBudgetFilters) {
    const globalFilters = useCategoryFilters().filters;
    const range = useDateRange();

    return useQuery<IFinOpsServiceGEMMapping[], Error>(
        [Endpoints.GetServiceGEMMapping, globalFilters.filters, range.startDate, range.endDate],
        () => getServiceGEMMapping(finOpsFilters)
    );
}

export function useUpdateMonthlyBudgetAllocation(
  onSuccess?: () => void,
  onError?: (error: Error) => void
) {
  return useMutation({
    mutationFn: (allocations: IFinOpsMonthlyBudgetAllocationRecord[]) => updateMonthlyBudgetAllocation(allocations),
    onSuccess,
    onError
  });
}

export function useGetUserApplicationRoles() {
    return useQuery<string[], Error>(
        [Endpoints.GetUserApplicationRoles],
        () => getUserApplicationRoles()
    );
}